import { useDocEngine, useGetDocumentWithAllVersions } from "@hooks";
import { Typography } from "@mui/material";
import { type MouseEvent, useCallback, useMemo } from "react";
import type { ChatToolMessage } from "../../types";
import { useChatDeviceContext } from "../../useChatDeviceContext";
import { ToolCallFrame } from "./ToolCallFrame";

interface ReadDocumentToolResultProps {
  args: Record<string, any>;
  toolResult?: ChatToolMessage;
  isLoading?: boolean;
}

function getDocumentIdFromPath(documentId: string): string {
  const segments = documentId.split("/").filter(Boolean);
  return segments[segments.length - 1] ?? documentId;
}

function parseToolResult(content: unknown): Record<string, any> | undefined {
  if (!content) return undefined;
  if (typeof content === "object" && !Array.isArray(content)) {
    return content as Record<string, any>;
  }
  if (typeof content !== "string") return undefined;
  if (!content.trimStart().startsWith("{")) return undefined;

  try {
    const parsed = JSON.parse(content);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, any>)
      : undefined;
  } catch {
    return undefined;
  }
}

function getStringValue(value: unknown): string {
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : "";
}

function getErrorMessage(
  toolResult: ChatToolMessage | undefined,
  parsedContent: Record<string, any> | undefined
): string {
  if (toolResult?.status !== "error") {
    return "";
  }

  const parsedError =
    getStringValue(parsedContent?.error) ||
    getStringValue(parsedContent?.message);

  if (parsedError) {
    return parsedError;
  }

  const rawContent = getStringValue(toolResult.content);
  return rawContent || "The document could not be read.";
}

export function ReadDocumentToolResult({
  args,
  toolResult,
  isLoading = false,
}: ReadDocumentToolResultProps) {
  const { orgId } = useChatDeviceContext();
  const parsedContent = useMemo(
    () => parseToolResult(toolResult?.content),
    [toolResult?.content]
  );
  const rawArgsDocumentId =
    getStringValue(args.document_id) || getStringValue(args.id);
  const resultDocumentId =
    getStringValue(parsedContent?.documentId) ||
    getStringValue(parsedContent?.tiptapDocumentId);
  const documentId = rawArgsDocumentId || resultDocumentId;
  const documentIdFromPath = documentId
    ? getDocumentIdFromPath(documentId)
    : "";
  const { data: document, isError: documentUnavailable } =
    useGetDocumentWithAllVersions(orgId, documentIdFromPath, {
      throwOnError: false,
    });
  const { navigateToQMSDocument } = useDocEngine(document?.deviceId ?? "all");
  const navigationDocumentId = document?.id ?? documentIdFromPath;
  const documentTitle = document?.title || documentIdFromPath || "document";
  const versionId =
    getStringValue(parsedContent?.versionId) ||
    getStringValue(parsedContent?.version_id) ||
    getStringValue(args.version_id) ||
    getStringValue(args.versionId);
  const hasError = toolResult?.status === "error";
  const errorMessage = getErrorMessage(toolResult, parsedContent);
  const titleVerb = hasError
    ? "Failed to read"
    : isLoading && !toolResult
      ? "Reading"
      : "Read";

  const handleOpenDocument = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      if (!navigationDocumentId) {
        return;
      }

      navigateToQMSDocument({
        documentId: navigationDocumentId,
        versionId: versionId || undefined,
      });
    },
    [navigateToQMSDocument, navigationDocumentId, versionId]
  );

  return (
    <ToolCallFrame
      title={
        <span className="inline-flex flex-wrap items-baseline gap-1 text-sm leading-5">
          <span className="text-sm leading-5">{titleVerb}</span>
          <button
            aria-label={`Open ${documentTitle}`}
            className="m-0 cursor-pointer rounded-sm border-0 bg-transparent p-0 text-sm leading-5 text-blue-600 no-underline underline-offset-2 hover:text-blue-800 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-default disabled:text-gray-500 disabled:no-underline"
            disabled={!document || documentUnavailable}
            onClick={handleOpenDocument}
            type="button"
          >
            {documentTitle}
          </button>
        </span>
      }
      isLoading={isLoading && !toolResult}
      hasError={hasError}
      isExpandable={false}
      hasToolResult={Boolean(toolResult)}
      hasContent={hasError}
    >
      {errorMessage ? (
        <Typography className="text-sm text-red-600" component="span">
          {errorMessage}
        </Typography>
      ) : null}
    </ToolCallFrame>
  );
}

ReadDocumentToolResult.displayName = "ReadDocumentToolResult";
